/**
 * Task #1763 — Pagina cross-tenant tenant users.
 *
 * Consuma il legacy `/api/master/users` (schema `public.users`, JOIN tenants):
 * lista utenti di tutti i tenant non-master, con filtri, edit, impersonation,
 * reset password. Le funzioni di gestione admin master (super_admin, ruoli,
 * permessi) vivono nella pagina sorella `MasterUsersMaster.tsx`.
 *
 * Note di compatibilità: la rotta legacy `/master/users` è stata sostituita da
 * `/master/users-tenant` (questa) + `/master/users-master`. Un redirect lato
 * router mantiene i link esistenti funzionanti per una release con banner
 * deprecation (`?legacy=1`) visibile finché l'utente non lo chiude.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  Users, Search, Edit, AlertCircle, Key, CheckCircle, UserCheck, Info, X,
} from 'lucide-react';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { UserAvatar } from '@easyflow/ui-master/components/ui/user-avatar';
import { RoleBadge } from '@easyflow/ui-master/components/ui/role-badge';
import { ConfirmActionModal } from '@easyflow/ui-master/components/ui/confirm-action-modal';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import type {
  CrossTenantUserListItem,
  CrossTenantTenantOption,
} from '@easyflow/shared';
import { PRODUCT_REGISTRY, DEFAULT_PRODUCT, getProductDefinition } from '@easyflow/shared';
import { resolveTenantOrigin } from '@/lib/productOrigins';

interface UserEditForm {
  first_name: string;
  last_name: string;
  email: string;
  role: 'admin' | 'agent' | 'user';
  status: 'active' | 'inactive' | 'suspended';
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return 'Mai';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const NATIVE_SELECT_CLASS =
  'px-3 py-2 border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500';

export default function TenantUsersMaster(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showLegacyBanner, setShowLegacyBanner] = useState<boolean>(
    () => searchParams.get('legacy') === '1' || (typeof window !== 'undefined' && window.location.pathname.endsWith('/users')),
  );

  const [users, setUsers] = useState<CrossTenantUserListItem[]>([]);
  const [tenants, setTenants] = useState<CrossTenantTenantOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') ?? '');
  const [selectedTenant, setSelectedTenant] = useState<string>('all');
  // Task #3148 — filtro prodotto ('all' | key da PRODUCT_REGISTRY).
  const [selectedProduct, setSelectedProduct] = useState<string>('all');

  const [editTarget, setEditTarget] = useState<CrossTenantUserListItem | null>(null);
  const [editForm, setEditForm] = useState<UserEditForm>({
    first_name: '', last_name: '', email: '',
    role: 'user', status: 'active',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [resetTarget, setResetTarget] = useState<CrossTenantUserListItem | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const [impersonateTarget, setImpersonateTarget] = useState<CrossTenantUserListItem | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  useEffect(() => { void loadData(); }, []);

  async function loadData(): Promise<void> {
    try {
      setIsLoading(true);
      setError('');
      const [usersResp, tenantsResp] = await Promise.all([
        apiClient.getCrossTenantUsers(),
        apiClient.getCrossTenantTenantOptions(),
      ]);
      if (usersResp.success && usersResp.data) {
        const list = Array.isArray(usersResp.data.users) ? usersResp.data.users : [];
        setUsers(list.filter((u) => !u.is_master_tenant));
      }
      if (tenantsResp.success && Array.isArray(tenantsResp.data)) {
        setTenants(tenantsResp.data.filter((t) => !t.is_master));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento utenti');
    } finally {
      setIsLoading(false);
    }
  }

  const filteredUsers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return users.filter((u) => {
      const matchesSearch =
        !q ||
        u.email.toLowerCase().includes(q) ||
        (u.first_name ?? '').toLowerCase().includes(q) ||
        (u.last_name ?? '').toLowerCase().includes(q) ||
        u.tenant_name.toLowerCase().includes(q) ||
        (u.legal_name ?? '').toLowerCase().includes(q) ||
        (u.vat_number ?? '').toLowerCase().includes(q);
      const matchesTenant = selectedTenant === 'all' || u.tenant_id === selectedTenant;
      // Task #3148 — filtro per prodotto/edition del tenant.
      const matchesProduct = selectedProduct === 'all' || (u.tenant_product ?? DEFAULT_PRODUCT) === selectedProduct;
      return matchesSearch && matchesTenant && matchesProduct;
    });
  }, [users, searchTerm, selectedTenant, selectedProduct]);

  function openEdit(user: CrossTenantUserListItem): void {
    setEditTarget(user);
    const role: UserEditForm['role'] =
      user.role === 'admin' || user.role === 'agent' || user.role === 'user'
        ? user.role : 'user';
    const status: UserEditForm['status'] =
      user.status === 'active' || user.status === 'inactive' || user.status === 'suspended'
        ? user.status : 'active';
    setEditForm({
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      email: user.email,
      role, status,
    });
    setEditError(null);
  }

  async function submitEdit(): Promise<void> {
    if (!editTarget) return;
    try {
      setEditSubmitting(true);
      setEditError(null);
      const resp = await apiClient.patch(`/master/users/${editTarget.id}`, editForm);
      if (!resp.success) throw new Error(resp.error ?? 'Aggiornamento fallito');
      setSuccessMessage('Utente aggiornato');
      setEditTarget(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Aggiornamento fallito');
    } finally {
      setEditSubmitting(false);
    }
  }

  /**
   * Reset password tenant user: il motivo è obbligatorio (audit log).
   * L'endpoint legacy attualmente lo accetta come campo opzionale; verrà reso
   * mandatorio anche server-side nel follow-up Task #1788 (allineamento con
   * il piano admin master che già impone reasonSchema 10..500).
   */
  async function submitReset(reason: string): Promise<void> {
    if (!resetTarget) return;
    try {
      setResetSubmitting(true);
      setResetError(null);
      const resp = await apiClient.post(`/master/users/${resetTarget.id}/reset-password`, { reason });
      if (!resp.success) throw new Error(resp.error ?? 'Reset fallito');
      setSuccessMessage(`Email di reset inviata a ${resetTarget.email}`);
      setResetTarget(null);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Reset fallito');
    } finally {
      setResetSubmitting(false);
    }
  }

  /**
   * Impersonation cross-tenant: reason opzionale ma raccomandato (audit log
   * Task #1788). Se l'admin lo inserisce viene loggato in
   * `audit_logs.metadata.reason` lato backend.
   */
  async function confirmImpersonate(reason: string): Promise<void> {
    if (!impersonateTarget) return;
    try {
      setImpersonating(true);
      setImpersonateError(null);
      const trimmed = reason.trim();
      const resp = await apiClient.impersonateUser(
        impersonateTarget.tenant_id,
        impersonateTarget.id,
        trimmed.length > 0 ? trimmed : undefined,
      );
      const tokenData = resp.data as { token?: string; impersonated_user?: { tenant_slug?: string } } | undefined;
      if (resp.success && tokenData?.token) {
        const tenantSlug = tokenData.impersonated_user?.tenant_slug ?? impersonateTarget.tenant_slug;
        const masterToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        // Task #3260 — origin product-aware del tenant (fallback piattaforma).
        const tenantOrigin = await resolveTenantOrigin(impersonateTarget.tenant_product);
        let url = `${tenantOrigin}/${tenantSlug}/auth/impersonate?token=${encodeURIComponent(tokenData.token)}`;
        if (masterToken) url += `&backup=${encodeURIComponent(masterToken)}`;
        setSuccessMessage(`Reindirizzamento a ${tenantSlug}...`);
        setTimeout(() => { window.location.href = url; }, 500);
      } else {
        throw new Error(resp.error ?? 'Impersonation fallita');
      }
    } catch (err) {
      setImpersonateError(err instanceof Error ? err.message : 'Impersonation fallita');
      setImpersonating(false);
    }
  }

  function dismissLegacyBanner(): void {
    setShowLegacyBanner(false);
    if (searchParams.get('legacy')) {
      const next = new URLSearchParams(searchParams);
      next.delete('legacy');
      setSearchParams(next, { replace: true });
    }
  }

  function statusBadge(user: CrossTenantUserListItem): JSX.Element {
    if (user.activation_status === 'pending_activation') {
      return (
        <Badge
          className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
          title="L'utente non ha ancora completato il primo accesso"
        >
          In attesa attivazione
        </Badge>
      );
    }
    switch (user.status) {
      case 'active':
        return <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Attivo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inattivo</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Sospeso</Badge>;
      default:
        return <Badge variant="outline">{user.status}</Badge>;
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50 dark:bg-slate-900 p-6">
        <div className="w-full">
          <div className="mb-6">
            <div className="h-8 w-64 bg-white/20 rounded animate-pulse mb-2" />
            <div className="h-4 w-96 bg-white/10 rounded animate-pulse" />
          </div>
          <SkeletonTable rows={10} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {showLegacyBanner && (
          <Alert className="mb-4 bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800">
            <Info className="h-4 w-4 text-cyan-600" />
            <AlertDescription className="text-cyan-900 dark:text-cyan-100 flex items-start justify-between gap-3">
              <span>
                <strong>URL aggiornato:</strong> la vecchia rotta <code>/master/users</code> è stata sostituita da
                {' '}<code>/master/users-tenant</code> (questa pagina) e <code>/master/users-master</code>.
                Aggiorna i tuoi segnalibri; il redirect resterà attivo per una release di transizione.
              </span>
              <button onClick={dismissLegacyBanner} className="shrink-0 p-1 rounded hover:bg-cyan-100 dark:hover:bg-cyan-800" title="Chiudi">
                <X className="h-4 w-4" />
              </button>
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Utenti tenant</h1>
            <p className="text-muted-foreground">
              Gestione cross-tenant utenti applicativi · {users.length} totali · {tenants.length} tenant
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {successMessage && (
          <Alert className="mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">{successMessage}</AlertDescription>
          </Alert>
        )}

        <GlassCard className="mb-6">
          <GlassCardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome, email, tenant, ragione sociale, P.IVA..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="w-64">
                <select
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  className={`w-full ${NATIVE_SELECT_CLASS}`}
                >
                  <option value="all">Tutti i tenant</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="w-48">
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className={`w-full ${NATIVE_SELECT_CLASS}`}
                >
                  <option value="all">Tutti i prodotti</option>
                  {PRODUCT_REGISTRY.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>
              <Users className="h-5 w-5 inline mr-2" />
              Utenti ({filteredUsers.length})
            </GlassCardTitle>
            <GlassCardDescription>
              Account applicativi nei tenant non-master. Gli admin master sono nella sezione dedicata.
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Utente</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Ruolo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Tenant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Stato</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Ultimo accesso</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground">Nessun utente trovato</td>
                    </tr>
                  ) : filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-white/5">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar firstName={user.first_name} lastName={user.last_name} email={user.email} size="md" />
                          <div className="min-w-0">
                            <div className="font-medium text-foreground truncate">{user.first_name} {user.last_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4"><RoleBadge role={user.role} variant="tenant" /></td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{user.tenant_name}</span>
                          <span className="text-xs text-muted-foreground">{user.tenant_slug}</span>
                          <Badge variant="outline" className="mt-0.5 w-fit text-[10px] px-1.5 py-0">
                            {getProductDefinition(user.tenant_product ?? DEFAULT_PRODUCT)?.label ?? user.tenant_product}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4">{statusBadge(user)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{formatDateTime(user.last_login_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(user)} title="Modifica">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => { setResetError(null); setResetTarget(user); }}
                            title="Invia email reset password">
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setImpersonateTarget(user)} title="Impersona utente">
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* EDIT MODAL */}
      <Dialog open={editTarget !== null} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Modifica utente tenant</DialogTitle>
            <DialogDescription>{editTarget?.tenant_name} · {editTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-first">Nome</Label>
                <Input id="edit-first" value={editForm.first_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="edit-last">Cognome</Label>
                <Input id="edit-last" value={editForm.last_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-role">Ruolo</Label>
                <select id="edit-role" value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as UserEditForm['role'] }))}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background">
                  <option value="admin">Admin</option>
                  <option value="agent">Agent</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div>
                <Label htmlFor="edit-status">Stato</Label>
                <select id="edit-status" value={editForm.status}
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value as UserEditForm['status'] }))}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background">
                  <option value="active">Attivo</option>
                  <option value="inactive">Inattivo</option>
                  <option value="suspended">Sospeso</option>
                </select>
              </div>
            </div>
            {editError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{editError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editSubmitting}>Annulla</Button>
            <Button onClick={() => void submitEdit()} disabled={editSubmitting} className="bg-cyan-600 hover:bg-cyan-700">
              {editSubmitting ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD — reason obbligatorio per audit log */}
      <ConfirmActionModal
        open={resetTarget !== null}
        title="Invia email reset password"
        description={resetTarget ? `Sarà inviata un'email di reset a ${resetTarget.email}. Indica il motivo dell'azione per l'audit log.` : ''}
        confirmLabel="Invia email"
        requireReason
        submitting={resetSubmitting}
        errorMessage={resetError}
        onConfirm={(reason) => submitReset(reason)}
        onCancel={() => setResetTarget(null)}
      />

      {/* IMPERSONATE CONFIRM — reason opzionale ma raccomandato (audit log) */}
      <ConfirmActionModal
        open={impersonateTarget !== null}
        title="Conferma impersonation"
        description={
          impersonateTarget
            ? `Accederai come ${impersonateTarget.first_name ?? ''} ${impersonateTarget.last_name ?? ''} (${impersonateTarget.email}) nel tenant ${impersonateTarget.tenant_name}. Indica il motivo per l'audit log (opzionale ma raccomandato).`
            : ''
        }
        confirmLabel="Conferma"
        showReason
        reasonLabel="Motivo (opzionale)"
        reasonPlaceholder="Es: supporto utente, verifica configurazione, debug ticket #..."
        submitting={impersonating}
        errorMessage={impersonateError}
        onConfirm={(reason) => confirmImpersonate(reason)}
        onCancel={() => { if (!impersonating) { setImpersonateTarget(null); setImpersonateError(null); } }}
      />
    </div>
  );
}
